import { Checkbox, Divider, Form } from 'antd';
import { useContext, useRef } from 'react';
import { useStore } from 'zustand';

import { inventoryStore } from 'app/components/pages/Extranet/ExtranetInventory/Modern/InventoryStore';
import { ConstantValueInput } from 'app/components/pages/Extranet/ExtranetInventory/Modern/_components/Common/ConstantValueInput/ConstantValueInput';
import { DailyConfigRow } from 'app/components/pages/Extranet/ExtranetInventory/Modern/_components/Common/DailyConfigRow';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import { capitalizeFirstLetter } from 'app/utils/string';
import { pluralize } from 'app/utils/strings';

import styles from './DailyStock.module.scss';

const BASE_PATH = ['openingConfiguration', 'stockConfiguration'];

type DailyStockRowProps = {
  currentValue: number;
  paths: { value: (string | number)[]; isDisabled: (string | number)[] };
  day: DAY_OF_WEEK;
};

const DailyStockRow = ({ currentValue, paths, day }: DailyStockRowProps) => {
  const { getExperience } = useContext(ModernHotelContext);
  const experienceId = useStore(
    inventoryStore,
    (state) => state.experienceStockConfig?.expId
  );

  const experience = getExperience(experienceId);

  const inputRef = useRef<HTMLInputElement>(null);
  const form = Form.useFormInstance();
  const stockValue = Form.useWatch(paths.value, form);

  const isUnlimited = stockValue === null || stockValue === undefined;

  const handleUnlimitedChange = (checked: boolean) => {
    if (checked) {
      form.setFieldValue(paths.value, null);
    } else {
      form.setFieldValue(paths.value, 0);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const isAlwaysIncludedWithNight = experience?.isAlwaysIncludedWithNight;

  return (
    <div className={styles.stockContent}>
      <Divider />
      {!isAlwaysIncludedWithNight && (
        <ConstantValueInput
          name={paths.value}
          label="Publish"
          suffix={`${pluralize(
            currentValue,
            'stock',
            'stocks',
            false
          )} every ${capitalizeFirstLetter(day)}`}
          inputRef={inputRef}
          placeholder="∞"
          isRequired={false}
        />
      )}
      <div className={styles.checkboxWrapper}>
        <div className={styles.checkboxRow}>
          {!isAlwaysIncludedWithNight && (
            <Checkbox
              checked={isUnlimited}
              onChange={(e) => handleUnlimitedChange(e.target.checked)}
            />
          )}
          <p className={styles.checkboxLabel}>Unlimited (∞)</p>
        </div>
      </div>
    </div>
  );
};

export const DailyStock = () => {
  return (
    <>
      {Object.values(DAY_OF_WEEK).map((day) => {
        return (
          <DailyConfigRow
            key={day}
            day={day}
            basePath={BASE_PATH}
            fieldNames={{
              isToggled: 'isToggled',
              value: 'stock',
              isDisabled: 'isDisabled',
            }}
            disabledText="Closed"
            hasDisabledText
          >
            {({ currentValue, paths, day: innerDay }) => (
              <DailyStockRow
                currentValue={currentValue as number}
                paths={paths}
                day={innerDay}
              />
            )}
          </DailyConfigRow>
        );
      })}
    </>
  );
};
